import {Store} from "@ngrx/store";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Injectable} from "@angular/core";
import {
    GetCompany,
    GetRestaurants, GetSubscription,
    RefreshCurrentRestaurant,
    SetCurrentRestaurant,
    SetRestaurants, SetSubscription,
    UpdateCompany
} from "./store.actions";
import {catchError, switchMap, withLatestFrom} from "rxjs/operators";
import {CompanyService} from "../services/company.service";
import {CompanyInterface} from "../interfaces/company.interface";
import {EMPTY, of} from "rxjs";
import {RestaurantService} from "../services/restaurant.service";
import {RestaurantInterface} from "../interfaces/restaurant.interface";
import {selectCurrentRestaurant} from "./store.selectors";
import {SubscriptionService} from "../../app/global/billings/services/subscription.service";
import {CompanySubscriptionInterface} from "../../app/global/billings/interfaces/company-subscription.interface";

@Injectable()
export class StoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private companyService: CompanyService,
    private restaurantService: RestaurantService,
    private subscriptionService: SubscriptionService
  ) {}

  public getCompany$ = createEffect(() => this.actions$.pipe(
      ofType(GetCompany),
      switchMap(action =>
          this.companyService.getInfo()
              .pipe(
                  switchMap((company: CompanyInterface) => of(
                      UpdateCompany({data: company})
                  )),
                  catchError(error => EMPTY)
              ))));
    public getRestaurants$ = createEffect(() => this.actions$.pipe(
        ofType(GetRestaurants),
        switchMap(action =>
            this.restaurantService.fetchAllRestaurants()
                .pipe(
                    switchMap((restaurants: RestaurantInterface[]) => of(
                        SetRestaurants({restaurants})
                    )),
                    catchError(error => EMPTY)
                ))));

    public refreshCurrentRestaurant$ = createEffect(() => this.actions$.pipe(
        ofType(RefreshCurrentRestaurant),
        withLatestFrom(this.store.select(selectCurrentRestaurant)),
        switchMap(([, restaurant]) =>
            this.restaurantService.getRestaurantInfo(restaurant.id)
                .pipe(
                    switchMap((restaurant: RestaurantInterface) => of(
                        SetCurrentRestaurant({restaurant})
                    )),
                    catchError(error => EMPTY)
                ))));

    public getSubscription$ = createEffect(() => this.actions$.pipe(
        ofType(GetSubscription),
        withLatestFrom(this.store.select(selectCurrentRestaurant)),
        switchMap(([, restaurant]) =>
            this.subscriptionService.getCompanySubscription()
                .pipe(
                    switchMap((subscription: CompanySubscriptionInterface) => {
                        if (!restaurant) {
                            return EMPTY;
                        }

                        const restaurantSub = subscription.restaurants.find(r => r.id === restaurant.id);

                        return of(
                            SetSubscription({subscription: {
                                    STATUS: subscription.status,
                                    WEBSITE: restaurantSub.subscription_items.WEBSITE.available,
                                    MOBILE_APP: restaurantSub.subscription_items.MOBILE_APP.available,
                                    LOYALTY_PROMO_CODES: restaurantSub.subscription_items.LOYALTY_PROMO_CODES.available,
                                    KDS: restaurantSub.subscription_items.KDS.available,
                                    INVENTORY: restaurantSub.subscription_items.INVENTORY.available,
                                    QR_MENU_TABLE_ORDER: restaurantSub.subscription_items.QR_MENU_TABLE_ORDER.available,
                                    FISCALIZATION: restaurantSub.subscription_items.FISCALIZATION.available,
                                    DRIVER: restaurantSub.subscription_items.DRIVER.available,
                                    KIOSK: restaurantSub.subscription_items.KIOSK.available,
                                    ERP: restaurantSub.subscription_items.ERP.available,
                                    CALLER: restaurantSub.subscription_items.CALLER?.available,
                         }})
                        )
                    }),
                    catchError(error => EMPTY)
                ))));
}
